// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { getAssetsInfo } from '@shared/data-access/transformers/assets/get-assets-info';
import { numberToFixed } from '@shared/ui-kit/utils';
import { selectIdeas } from '@advisor-app/store/slices/my-research';
import {
  selectRebalanceConfig,
  updateRebalanceConfig,
} from '@advisor-app/store/slices/parameters';
import {
  selectCurrentPortfolio,
  selectTargetPortfolio,
  portfolioPositionsSlice,
} from '@shared/data-access/slices/portfolio-positions';
import { useAppDispatch, useAppSelector } from '@advisor-app/store/store';
import { type SelectChangeEvent } from '@mui/material';
import type { AccountCurrency } from '@shared/data-access/types/brokerage-types';
import { type OrderStrategy } from '@shared/data-access/interfaces/redux/portfolio-rebalance-state';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { type KpiBoxesProps, SectionContainer } from '@shared/ui-kit/components';
import { type FC, useEffect, useMemo, useState } from 'react';
import { KpisRebalance } from './kpis-rebalance';
import { RebalancePortfolioEditor } from './rebalance-portfolio-editor';
import { RebalancePortfolioTable } from './rebalance-portfolio-table';
import { RebalanceSelector } from './rebalance-selector';
import { RebalanceSliderCash } from './rebalance-slider-cash';
import { TaxSystemSelector } from './tax-systems-selector';
import { useTranslation } from 'react-i18next';

interface RebalanceEditorProps {
  investment: number;
  deviation: number;
  handleInvestmentChange: (value: number) => void;
  handleDeviationChange: (value: number) => void;
  handleOrderStrategyChange: ({ target }: SelectChangeEvent) => void;
  isOpen: boolean;
  handleModal: () => void;
  orderStrategy: OrderStrategy;
}

export const RebalanceEditor: FC<RebalanceEditorProps> = ({
  investment,
  deviation,
  handleInvestmentChange,
  handleDeviationChange,
  handleOrderStrategyChange,
  isOpen,
  handleModal,
  orderStrategy,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const cashSliderTitles = {
    deposit: t('rebalance.deposit', { ns: 'rebalance' }),
    withdrawal: t('rebalance.withdrawal', { ns: 'rebalance' }),
    adjustStrategy: t('rebalance.additional-deposit', { ns: 'rebalance' }),
  };

  const rebalance = useAppSelector(selectRebalanceConfig);
  const {
    amount: newInvestment,
    type: rebalanceType,
    enableTransfers = false,
  } = rebalance;

  const targetPortfolio = useAppSelector(selectTargetPortfolio);
  const investmentPortfolio = useAppSelector(selectCurrentPortfolio);

  const ideasSaved = useAppSelector(selectIdeas);
  const currency = useAppSelector(selectAccountCurrency);

  const prefix = useMemo(() => {
    const currencySymbols: { [key in AccountCurrency]: string } = {
      USD: '$',
      EUR: '€',
    };
    return currencySymbols[currency];
  }, [currency]);

  const [portfolioSelected, setPortfolioSelected] = useState<string>('');

  const { totalInvestment, totalUnrealizedGains, harvestOpportunity } =
    useMemo(() => {
      return investmentPortfolio.reduce(
        (acc, curr) => {
          acc.totalInvestment += curr.qty * curr.price;

          acc.totalUnrealizedGains += curr.unrealized_capital_gains;

          if (curr.unrealized_capital_gains < 0) {
            acc.harvestOpportunity += curr.unrealized_capital_gains;
          }

          return acc;
        },
        { totalInvestment: 0, totalUnrealizedGains: 0, harvestOpportunity: 0 },
      );
    }, [investmentPortfolio]);

  const kpisData: KpiBoxesProps[] = [
    {
      value: totalInvestment,
      text: t('common.investment', { ns: 'common' }),
      numberPositions: investmentPortfolio.length,
      icon: 'TrendingUp',
      color: 'success',
    },
    {
      value: newInvestment,
      text: t('rebalance.new-investment', { ns: 'rebalance' }),
      icon: 'Plus',
      color: 'main',
    },
    {
      value: totalUnrealizedGains,
      text: t('rebalance.unrealized-gains', { ns: 'rebalance' }),
      popupName: 'kpis.unrealizedGains',
      icon: 'DollarSign',
      color: 'main',
    },
    {
      value: harvestOpportunity,
      text: t('rebalance.harvest-opportunity', { ns: 'rebalance' }),
      popupName: 'kpis.harvestOpportunity',
      icon: 'Zap',
      color: 'success',
    },
  ];

  const displayInvestmentPortfolio = useMemo(() => {
    return investmentPortfolio
      .map(data => {
        return {
          ticker: data.ticker,
          qty: data.qty * data.price,
          weight: numberToFixed(((data.qty * data.price) / totalInvestment) * 100),
          details: data.details,
        };
      })
      .sort((a, b) => b.weight - a.weight);
  }, [investmentPortfolio, totalInvestment]);

  useEffect(() => {
    if (portfolioSelected) {
      const selectedIdea = ideasSaved.filter(
        idea => idea.portfolio === portfolioSelected,
      )[0];
      const totalInvestmentIdea = selectedIdea.holdings.reduce(
        (prev, curr) => prev + curr.weight,
        0,
      );
      const newTargetStrategy = selectedIdea.holdings
        .map(({ ticker, weight: outstanding_balance }) => {
          return {
            ticker: ticker,
            weight:
              Math.round((outstanding_balance / totalInvestmentIdea) * 100 * 1000) /
              1000,
          };
        })
        .sort((a, b) => b.weight - a.weight);

      dispatch(
        portfolioPositionsSlice.actions.setTargetPortfolioPositions(
          newTargetStrategy,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioSelected]);

  /* istanbul ignore next */
  useEffect(() => {
    const targetTickers = targetPortfolio
      .filter(position => !position?.details)
      .map(position => position.ticker);

    if (targetTickers.length > 0) {
      getAssetsInfo(targetTickers, dispatch)
        .then(data => {
          const detailedTargetPortfolio = targetPortfolio.map(position => {
            const assetInfo =
              position.details ??
              data.find(asset => asset.ticker === position.ticker);

            return { ...position, details: assetInfo };
          });

          dispatch(
            portfolioPositionsSlice.actions.setTargetPortfolioPositions(
              detailedTargetPortfolio,
            ),
          );
        })
        .catch(err => console.log('[RebalanceEditor]: ', err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetPortfolio.length]);

  return (
    <div className="grid !gap-4">
      {rebalanceType === 'withdrawal' && (
        <div className="mb-4 flex items-center gap-x-6">
          <TaxSystemSelector
            isOpen={isOpen}
            handleModal={handleModal}
            handleOrderStrategyChange={handleOrderStrategyChange}
            orderStrategy={orderStrategy}
          />
        </div>
      )}
      <KpisRebalance kpisData={kpisData} />

      <RebalanceSelector rebalanceType={rebalanceType} />

      <SectionContainer className="grid grid-cols-2 gap-4">
        <RebalanceSliderCash
          title={cashSliderTitles[rebalanceType as keyof typeof cashSliderTitles]}
          placeholder="$"
          prefix={prefix}
          value={investment}
          totalInvestment={totalInvestment}
          rebalanceType={rebalanceType}
          onChangeInputValue={value => {
            handleInvestmentChange(value);
            dispatch(updateRebalanceConfig({ amount: value }));
          }}
          onChangeSlider={handleInvestmentChange}
          onChangeCommitted={() =>
            dispatch(updateRebalanceConfig({ amount: investment }))
          }
        />
        {rebalanceType === 'withdrawal' && (
          <RebalanceSliderCash
            percentageSlider
            title={t('rebalance.maximum-deviation', { ns: 'rebalance' })}
            placeholder="%"
            suffix="%"
            value={deviation}
            onChangeInputValue={handleDeviationChange}
            onChangeSlider={handleDeviationChange}
          />
        )}
      </SectionContainer>

      <SectionContainer className="grid grid-cols-1 gap-12 xl:grid-cols-2 xl:gap-16 2xl:gap-20">
        <RebalancePortfolioTable
          data={displayInvestmentPortfolio}
          currency={currency}
          enableTransfers={enableTransfers}
          onEnableTransfersChange={checked =>
            dispatch(updateRebalanceConfig({ enableTransfers: checked }))
          }
        />
        <RebalancePortfolioEditor
          data={[...targetPortfolio]}
          portfolios={ideasSaved.map(idea => idea.portfolio)}
          onChangePortfolioSelected={setPortfolioSelected}
          portfolioSelected={portfolioSelected}
          rebalanceType={rebalanceType}
          investment={totalInvestment}
          rebalanceCash={newInvestment}
          currency={currency}
        />
      </SectionContainer>
    </div>
  );
};
